import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Anchor, SimpleGrid, Stack, Text } from '@mantine/core';

import { generalRoutes } from 'constants/routes';

import { RootState } from 'store';
import { userIsAdmin, userIsSuperAdmin } from 'store/user/selectors';
import { User } from 'store/user/types';

interface Props {
  user: Partial<User>;
  agencyName: string | undefined;
  agencyId: number | undefined;
}

const BillingUserDetails = ({ user, agencyName, agencyId }: Props) => {
  const { t } = useTranslation();

  const { isAdmin } = useSelector((state: RootState) => ({
    isAdmin: userIsAdmin(state) || userIsSuperAdmin(state),
  }));

  return (
    <SimpleGrid cols={3}>
      <Stack>
        <Text fw={600}>{t('agency')}</Text>
        {isAdmin && agencyId ? (
          <Anchor
            fw="bold"
            href={generalRoutes.routes.agency(agencyId)}
            target="_blank"
            underline="hover"
            size="sm"
          >
            {agencyName}
          </Anchor>
        ) : (
          <Text size="sm">{agencyName}</Text>
        )}
      </Stack>
      <Stack>
        <Text fw={600}>{t('firstName')}</Text>
        <Text size="sm">{user.first_name}</Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('lastName')}</Text>
        <Text size="sm">{user.last_name}</Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('email')}</Text>
        <Text truncate="end" size="sm">
          {user.email}
        </Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('cellPhone')}</Text>
        <Text size="sm">{user.cell}</Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('contractStatus')}</Text>
        <Text size="sm">{user.eula_accepted && t('signed')}</Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('billingAddress')}</Text>
        <Text truncate="end" size="sm">
          {user.billing_addresses && user.billing_addresses[0]?.street}
        </Text>
      </Stack>
      <Stack>
        <Text fw={600}>
          {t('city')} &amp; {t('state')}
        </Text>
        <Text truncate="end" size="sm">
          {user.billing_addresses && user.billing_addresses[0]?.city}{' '}
          {user.billing_addresses && user.billing_addresses[0]?.state}
        </Text>
      </Stack>
      <Stack>
        <Text fw={600}>{t('zipcode')}</Text>
        <Text truncate="end" size="sm">
          {user.billing_addresses && user.billing_addresses[0]?.zip}
        </Text>
      </Stack>
    </SimpleGrid>
  );
};

export default BillingUserDetails;
